import {
  AnimatePresence,
  HTMLMotionProps,
  motion,
  useMotionValue,
  useTransform,
} from "motion/react";
import { css } from "@emotion/react";
import { useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import { Button } from "../Button";
import { Paper } from "../Paper";
import { useTheme } from "../theme";

type Side = "right" | "left";

interface DragHandlerProps extends HTMLMotionProps<"div"> {
  side: Side;
  min: number;
}

const DRAG_HANDLER_WIDTH = 12;

const DragHandler = ({ side, min, style, ...props }: DragHandlerProps) => {
  return (
    <motion.div
      css={css`
        position: fixed;
        top: 0;
        width: ${DRAG_HANDLER_WIDTH}px;
        height: 100%;
        cursor: col-resize;
        background-color: transparent;
      `}
      style={{
        [side]: min - DRAG_HANDLER_WIDTH,
        ...style,
      }}
      {...props}
    />
  );
};

interface PeekPanelProps<T> {
  /**
   * 閉じるボタンを押したときのコールバック
   * これを渡さない場合、閉じるボタンを表示されません。
   */
  onClose?: (flag: false) => void;
  /**
   * コンポーネント内に表示するコンテンツ
   */
  renderContent: (props: { data?: T }) => React.JSX.Element;
  /**
   * renderContentに渡されるデータ
   */
  data?: T;
}

const min = 280;
const side = "right" as Side;

const PEEK_CACHE_KEY = "matsuri-ui-peek-cache";

const setCache = (side: Side, offset: number) => {
  localStorage.setItem(
    PEEK_CACHE_KEY,
    JSON.stringify({
      side,
      offset,
      innerWidth: window.innerWidth,
    }),
  );
};

const getCacheOffset = (side: Side) => {
  const cache = localStorage.getItem(PEEK_CACHE_KEY);
  if (cache === null) {
    return null;
  }
  const data = JSON.parse(cache) as {
    offset: number;
    innerWidth: number;
    side: Side;
  };
  if (data.innerWidth !== window.innerWidth || data.side !== side) {
    return null;
  }
  return data.offset;
};

const PeekPanel = <T,>({
  onClose,
  data,
  renderContent: Content,
}: PeekPanelProps<T>) => {
  const max = useRef(window.innerWidth).current;
  const defaultWidth = useRef(Math.min(480, max)).current;

  const theme = useTheme();

  const mvOffset = useMotionValue(0);

  const mvOffsetCache = getCacheOffset(side);

  mvOffset.set(
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    mvOffsetCache === null
      ? side === "left"
        ? defaultWidth + min
        : min - defaultWidth
      : mvOffsetCache,
  );

  const mvWidth = useTransform(mvOffset, (v) => {
    setCache(side, v);
    return side === "left" ? v + min : min - v;
  });

  const [isDragging, setIsDragging] = useState(false);

  return (
    <motion.div
      css={css`
        position: fixed;
        top: 0px;
        z-index: ${theme.zIndex.drawer};
        flex-shrink: 0;
        height: 100%;
      `}
      style={{
        width: mvWidth,
        [side]: 0,
      }}
      initial={{
        width: 0,
      }}
      transition={{ ease: "easeOut", duration: 0.15 }}
      animate={{
        width: mvWidth.get(),
      }}
      exit={{
        width: 0,
      }}
    >
      <Paper
        elevation={5}
        css={css`
          display: flex;
          flex-direction: column;
          width: 100%;
          height: 100%;
          overflow: auto;
          transition: opacity ${theme.transitions.duration.shortest}
            ${theme.transitions.easing.easeOut};
        `}
        style={{
          opacity: isDragging ? 0.8 : 1,
        }}
      >
        <DragHandler
          side={side}
          style={{ x: mvOffset }}
          drag="x"
          min={min}
          dragElastic={0.025}
          dragConstraints={{
            left: side === "left" ? 0 : min - max,
            right: side === "left" ? max - min : 0,
          }}
          dragMomentum={false}
          variants={{
            active: {
              backgroundColor: theme.palette.action.hover,
            },
          }}
          onDragStart={() => {
            setIsDragging(true);
          }}
          onDragEnd={() => {
            setIsDragging(false);
          }}
          whileTap="active"
          whileHover="active"
        />
        <div
          css={css`
            position: sticky;
            top: 0px;
            z-index: ${theme.zIndex.drawer};
            padding: 8px;
          `}
        >
          {onClose ? (
            <Button
              title="閉じる"
              css={css`
                z-index: ${theme.zIndex.tooltip};
                color: ${theme.palette.text.secondary};
              `}
              icon={<CloseIcon fontSize="large" color="inherit" />}
              type="button"
              onClick={() => {
                onClose(false);
              }}
            />
          ) : null}
        </div>
        <div
          css={css`
            padding: 16px 24px 64px;
          `}
        >
          <Content data={data} />
        </div>
      </Paper>
    </motion.div>
  );
};

export interface PeekProps<T> extends PeekPanelProps<T> {
  /**
   * このコンポーネントを開くかどうか
   */
  open: boolean;
}

export const Peek = <T,>({ open, ...props }: PeekProps<T>) => {
  return (
    <AnimatePresence>{open ? <PeekPanel {...props} /> : null}</AnimatePresence>
  );
};
